import { check } from 'failed-to-load';
import { MAX_ERRORS_PER_PAGE } from './constants';

/**
 * A page error queue/history manager.
 * @property {Object[]} queue A queue of errors that were supposed to be reported before the page was completely loaded.
 * @property {Set} history We keep a history of reported errors to make sure we don't report the same error twice on the same page.
 * @property {Boolean} failedToLoadDependencies Whether the current page failed to load "critical" dependencies or not.
 */
export class ErrorManager {
    constructor() {
        this.queue = [];
        this.history = [];
        this.failedToLoadDependencies = false;
    }

    /**
     * Checks whether the provided error message should be reported or not. An error will be reported if all of the following apply:
     * - The record is an object with a "message" property.
     * - The current page loaded all "critical" dependencies.
     * - The current page reported less than the maximum amount we're willing to report.
     * - It hasn't been reported yet.
     * @param {ErrorRecord} record The record to send.
     * @return {Boolean}
     */
    shouldReport(record) {
        if (
            typeof record !== 'object' ||
            !record.message ||
            this.failedToLoadDependencies ||
            this.history.length >= MAX_ERRORS_PER_PAGE ||
            this.alreadySent(record.message)
        ) {
            return false;
        }

        return true;
    }

    /**
     * Check if an error with the same message was already sent
     * @param {string}
     * @returns {boolean}
     */
    alreadySent(message) {
        const sent = this.history.indexOf(message) !== -1;
        if (sent) { return true; }

        this.addToHistory(message);
        return false;
    }

    /**
     * Adds an error object to the queue, to be used in a later date.
     * @param {ErrorRecord} record The record object.
     */
    addToQueue(record) {
        this.queue.push(record);
    }

    /**
     * Adds an error message to the error history, so we can avoid reporting the same error multiple times.
     * @param {string} message The "message" property.
     */
    addToHistory(message) {
        this.history.push(message);
    }

    /**
     * Iterates over the error queue, and executes a provided function on each error object.
     * @param {Function} fn The function to execute.
     */
    executeQueue(fn) {
        this.queue.forEach(fn);
    }

    checkFailedToLoadDependencies() {
        const missing = check();

        if (!missing.length) { return; }

        this.failedToLoadDependencies = true;

        const list = missing.map(
            ({ src }) => src
        ).join(', ');

        // Clear the queue so only this error gets reported.
        this.queue = [{
            message: 'Fail to load dependency files before page load',
            code: 'DEPENDENCY_NOT_LOADED_ERROR',
            level: 'warn',
            details: {
                files: list,
                amount: missing.length
            }
        }];
    }
}

export default new ErrorManager();
